import { Link } from "react-router-dom";
import { Check, ShieldCheck, CreditCard, FileText, HelpCircle } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import SEO, { orgSchema, breadcrumbSchema, faqSchema } from "@/components/SEO";
import { PageHero, SectionHeading, FinalCTA } from "@/components/sections";
import { Reveal, stagger, item } from "@/components/motion";
import { motion } from "framer-motion";
import { INSURANCE } from "@/data/content";
import { useAppointment } from "@/components/AppointmentContext";

const FEES = [
  { icon: FileText, t: "Initial Assessment", d: "A comprehensive first session to understand your needs and set goals.", p: "Covered by most insurance" },
  { icon: CreditCard, t: "Individual Session", d: "50-minute evidence-based CBT sessions, in-person or via Telehealth.", p: "Covered by most insurance" },
  { icon: ShieldCheck, t: "Couples / Family", d: "Sessions focused on communication, connection, and repair.", p: "Contact us to verify" },
];

const FEE_FAQS = [
  { q: "How do I know if you accept my insurance?", a: "We accept most major insurance plans. The easiest way to confirm is to contact us with your insurance details — we'll verify your specific benefits, including any copay or deductible, before your first visit." },
  { q: "What if you're out-of-network for my plan?", a: "If we're out-of-network, we can provide a detailed superbill you can submit to your insurer for potential reimbursement. Many plans offer partial out-of-network coverage. We're happy to explain your options." },
  { q: "Do you offer private-pay rates?", a: "Yes. We offer transparent private-pay rates for clients who prefer not to use insurance or whose plans we don't accept. Contact us for current rates." },
  { q: "What is your cancellation policy?", a: "We ask for at least 24 hours' notice to cancel or reschedule, so we can offer the time to another client. Late cancellations may incur a fee, which we'll always explain clearly in advance." },
  { q: "Is Telehealth covered the same as in-person?", a: "In most cases, yes. Since the pandemic, the majority of insurers cover Telehealth at the same rate as in-person sessions. We'll verify this for your specific plan." },
];

export default function InsuranceFees() {
  const { openDialog } = useAppointment();
  const crumbs = [{ name: "Home", path: "/" }, { name: "Insurance & Fees", path: "/insurance-and-fees" }];
  return (
    <>
      <SEO title="Insurance & Fees" path="/insurance-and-fees"
        description="Beacon Counseling accepts most major insurance plans including Aetna, Anthem, BCBS, Cigna & UnitedHealthcare. Transparent fees and Telehealth coverage in Connecticut."
        jsonLd={[orgSchema, breadcrumbSchema(crumbs), faqSchema(FEE_FAQS)]} />
      <PageHero eyebrow="Insurance & Fees" breadcrumb={crumbs}
        title="Great care should be accessible."
        intro="We accept most major insurance plans and keep our fees transparent — so cost never stands between you and the support you deserve."
        image="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" />

      {/* Insurance logos */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading center eyebrow="Accepted Plans" title="Most major insurance accepted."
            intro="We'll verify your specific benefits before your first visit — just reach out with your plan details." />
          <div className="mx-auto mt-14 grid max-w-4xl grid-cols-2 gap-4 sm:grid-cols-4">
            {INSURANCE.map((n, i) => (
              <Reveal key={n} delay={i * 0.04}>
                <div className="flex min-h-[5.5rem] items-center justify-center rounded-2xl border border-navy-100 bg-white px-4 py-3 text-center text-sm font-medium leading-snug text-navy/70 shadow-soft transition-colors hover:text-teal">{n}</div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 text-center text-sm text-muted-foreground">Don't see your plan? <button onClick={openDialog} className="font-medium text-teal hover:underline">Contact us</button> — we may still be able to help.</p>
          </Reveal>
        </div>
      </section>

      {/* Fees */}
      <section className="bg-secondary py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading center eyebrow="Sessions & Fees" title="Simple, transparent, and mostly covered." />
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-14 grid gap-6 md:grid-cols-3">
            {FEES.map((f) => (
              <motion.div key={f.t} variants={item} className="rounded-3xl border border-navy-100 bg-white p-8 shadow-soft">
                <span className="grid h-13 w-13 place-items-center rounded-2xl bg-softblue/60 p-3 text-teal"><f.icon className="h-6 w-6" /></span>
                <h3 className="mt-5 font-display text-2xl text-navy">{f.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.d}</p>
                <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-teal/10 px-4 py-1.5 text-sm font-medium text-teal"><Check className="h-4 w-4" />{f.p}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-3xl px-6 lg:px-12">
          <SectionHeading center eyebrow="Questions" title="Insurance & fees, explained." />
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-12">
              {FEE_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`} className="border-navy-100">
                  <AccordionTrigger className="text-left font-display text-lg text-navy hover:text-teal hover:no-underline">
                    <span className="flex items-center gap-3"><HelpCircle className="h-5 w-5 shrink-0 text-teal" />{f.q}</span>
                  </AccordionTrigger>
                  <AccordionContent className="pl-8 text-base text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <FinalCTA title="Let's verify your coverage." text="Reach out and we'll confirm your benefits and answer any questions — with zero obligation." />
    </>
  );
}
